import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { eventTarget } from '@cornerstonejs/core';
import { annotation, Enums as ToolEnums } from '@cornerstonejs/tools';
import { selectAnnotationToMeasurement } from '@/state/slices/measurementSession';
import { useGetWorksheetCanonicalMeasurementsQuery } from '@/state/slices/options';
import {
  useGetStudyReportsQuery,
  useGetWorksheetQuery,
} from '@/state/slices/reports';
import { SettingsContext } from '@/state/contexts';
import {
  formatAnnotationValue,
  type AnnotationValueData,
} from '../../../utils/formatAnnotationValue';
import { useViewerContext } from '../../../ViewerContext';
import { orderRows, type ListRow } from '../utils/orderRows';

const EVENTS = [
  ToolEnums.Events.ANNOTATION_ADDED,
  ToolEnums.Events.ANNOTATION_COMPLETED,
  ToolEnums.Events.ANNOTATION_MODIFIED,
  ToolEnums.Events.ANNOTATION_REMOVED,
  ToolEnums.Events.ANNOTATION_VISIBILITY_CHANGE,
];

/** Zero-based frame for this image's annotation; null when it belongs elsewhere. */
function frameOf(
  refImageId: string | undefined,
  dicomUuid: string,
  stackImageId: string | null
): number | null {
  if (!refImageId) return null;
  const prefix = `video:${dicomUuid}/frames/`;
  if (refImageId.startsWith(prefix)) {
    const frame = Number(refImageId.slice(prefix.length)) - 1;
    return Number.isInteger(frame) && frame >= 0 ? frame : null;
  }
  return stackImageId && refImageId === stackImageId ? 0 : null;
}

/** Every measurement annotation on this image, as list rows, from Cornerstone's
 *  live state — what is drawn, including adoptions, edits and deletions. */
export function useImageAnnotations(
  dicomUuid: string,
  stackImageId: string | null
): ListRow[] {
  const annotationToMeasurement = useSelector(selectAnnotationToMeasurement);
  const { settings } = useContext(SettingsContext);
  const { data: worksheet } = useGetWorksheetQuery(
    settings.defaultWorksheet || '',
    { skip: !settings.defaultWorksheet }
  );
  const { data: canonicals } = useGetWorksheetCanonicalMeasurementsQuery();
  const { studyUuid } = useViewerContext();
  const { data: reports } = useGetStudyReportsQuery(studyUuid, {
    skip: !studyUuid,
  });
  const [rows, setRows] = useState<ListRow[]>([]);
  const signatureRef = useRef('');

  const reportValue = useMemo(
    () =>
      new Map(
        (reports?.results?.[0]?.measurements ?? []).map((m) => [
          m.canonicalMeasurement,
          m.value,
        ])
      ),
    [reports]
  );

  const { worksheetIndex, worksheetLabel } = useMemo(() => {
    const index = new Map<string, number>();
    const label = new Map<string, string>();
    worksheet?.measurementGroups?.forEach((g) =>
      g.measurementLabels.forEach((ml) => {
        if (!ml.canonicalMeasurement || index.has(ml.canonicalMeasurement))
          return;
        index.set(ml.canonicalMeasurement, index.size);
        label.set(ml.canonicalMeasurement, ml.label);
      })
    );
    return { worksheetIndex: index, worksheetLabel: label };
  }, [worksheet]);

  useEffect(() => {
    // A re-read queued for the previous image must not land after a switch.
    let active = true;
    const recompute = () => {
      if (!active) return;
      const next: ListRow[] = [];
      annotation.state.getAllAnnotations().forEach((a) => {
        const uid = a.annotationUID;
        const canonical = uid ? annotationToMeasurement[uid] : undefined;
        const frame = frameOf(
          (a.metadata as { referencedImageId?: string })?.referencedImageId,
          dicomUuid,
          stackImageId
        );
        if (!uid || !canonical || frame === null) return;
        const data = a.data as AnnotationValueData & {
          isAI?: boolean;
          abbreviation?: string;
        };
        const def = canonicals?.find((c) => c.uuid === canonical);
        const isAI = data?.isAI === true;
        // A manual trace without stats (e.g. an area on a video) shows the report's value.
        const saved = isAI ? null : reportValue.get(canonical);
        next.push({
          uid,
          frame,
          canonical,
          isAI,
          label:
            worksheetLabel.get(canonical) ??
            data?.abbreviation ??
            def?.name ??
            'Measurement',
          value:
            formatAnnotationValue(data, def) ||
            (saved == null
              ? ''
              : `${saved.toFixed(def?.precision ?? 2)} ${def?.unit ?? ''}`.trim()),
          hidden: annotation.visibility.isAnnotationVisible(uid) === false,
        });
      });
      const ordered = orderRows(next, worksheetIndex);
      // ANNOTATION_MODIFIED fires continuously during a drag.
      const nextSignature = JSON.stringify(ordered);
      if (nextSignature === signatureRef.current) return;
      signatureRef.current = nextSignature;
      setRows(ordered);
    };

    // AI adoption flips `isAI` on mouse-up without always emitting an event.
    const onMouseUp = () => requestAnimationFrame(recompute);

    recompute();
    EVENTS.forEach((evt) => eventTarget.addEventListener(evt, recompute));
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      EVENTS.forEach((evt) => eventTarget.removeEventListener(evt, recompute));
      active = false;
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [
    dicomUuid,
    stackImageId,
    annotationToMeasurement,
    canonicals,
    reportValue,
    worksheetIndex,
    worksheetLabel,
  ]);

  return rows;
}
